
import { Brain, BookOpen, Activity, Users } from 'lucide-react'

export function EspecialistasSection() {
  const especialidades = [
    {
      title: "Condiciones del Neurodesarrollo y Capacidades Intelectuales",
      description: "TDAH, Trastorno del Espectro Autista (TEA), Discapacidad Intelectual, Altas Capacidades Intelectuales, Enfermedades Raras y otras condiciones que afectan el desarrollo neurológico.",
      icon: Brain,
      href: "/condiciones-del-desarrollo",
      color: "neuro-blue"
    },
    {
      title: "Condiciones del Aprendizaje",
      description: "Dislexia, Discalculia, Disgrafía, Trastorno del Aprendizaje No Verbal y otras condiciones específicas que afectan el aprendizaje académico y el rendimiento escolar.",
      icon: BookOpen,
      href: "/condiciones-del-desarrollo",
      color: "neuro-yellow"
    },
    {
      title: "Condiciones Motoras",
      description: "Trastorno del Desarrollo de la Coordinación, Trastornos de Tics y otras condiciones que afectan el movimiento y la coordinación motora.",
      icon: Activity,
      href: "/condiciones-del-desarrollo",
      color: "neuro-green"
    },
    {
      title: "Condiciones del Comportamiento",
      description: "Trastorno Negativista Desafiante, Trastorno de Conducta y otras condiciones en la regulación conductual y emocional.",
      icon: Users,
      href: "/condiciones-del-desarrollo",
      color: "neuro-red"
    }
  ]

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'neuro-red':
        return 'bg-neuro-red bg-opacity-20 text-neuro-red'
      case 'neuro-yellow':
        return 'bg-neuro-yellow bg-opacity-20 text-neuro-yellow'
      case 'neuro-blue':
        return 'bg-neuro-blue bg-opacity-20 text-neuro-blue'
      case 'neuro-green':
        return 'bg-neuro-green bg-opacity-20 text-neuro-green'
      default:
        return 'bg-neuro-blue bg-opacity-20 text-neuro-blue'
    }
  }

  return (
    <section className="py-20 bg-white relative">
      {/* Fondo con degradado más suave y formas decorativas */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50/80 via-white/90 to-white z-0"></div>
      
      {/* Círculos decorativos con colores más visibles */}
      <div className="absolute top-10 right-10 w-32 h-32 bg-neuro-blue opacity-15 rounded-full hidden md:block"></div>
      <div className="absolute bottom-40 left-10 w-48 h-48 bg-neuro-red opacity-15 rounded-full hidden md:block"></div>
      <div className="absolute top-40 left-1/4 w-24 h-24 bg-neuro-yellow opacity-15 rounded-full hidden md:block"></div>
      
      {/* Patrón de ondas mejorado - usando los colores del logo en orden correcto */}
      <div className="absolute top-0 left-0 w-full overflow-hidden z-0">
        <div className="relative">
          {/* Onda decorativa simplificada y mejorada */}
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="w-full h-[80px]"
            style={{ fill: 'url(#waveGradient)' }}
          >
            <defs>
              <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f42455" stopOpacity="0.2" />
                <stop offset="25%" stopColor="#ffb700" stopOpacity="0.2" />
                <stop offset="50%" stopColor="#00a6c4" stopOpacity="0.2" />
                <stop offset="75%" stopColor="#95c85d" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#004b66" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"></path>
          </svg>
        </div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="relative mb-16">
          <div className="absolute -top-6 -left-6 w-20 h-20 bg-neuro-blue opacity-10 rounded-tl-3xl hidden md:block"></div>
          <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-neuro-red opacity-10 rounded-br-3xl hidden md:block"></div>
          
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-neuro-dark-blue mb-6">
              Especialistas en Salud Infantil Integral
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              En Neuro Peques ofrecemos atención especializada integral para población infanto-juvenil, con énfasis en neuropsicología, desarrollo del lenguaje, habilidades ocupacionales y bienestar emocional.
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {especialidades.map((especialidad, index) => {
            const IconComponent = especialidad.icon
            const colorClasses = getColorClasses(especialidad.color)
            return (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 transform transition-all duration-300 hover:scale-105">
                <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${colorClasses}`}>
                  <IconComponent className="h-10 w-10" />
                </div>
                <h3 className="text-2xl font-bold text-neuro-dark-blue mb-4">{especialidad.title}</h3>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">{especialidad.description}</p>
                <a 
                  href={especialidad.href}
                  className="text-neuro-dark-blue hover:text-neuro-blue font-semibold transition-colors inline-flex items-center group text-lg"
                >
                  Saber más →
                </a>
              </div>
            )
          })}
        </div>
        
        <div className="mt-16 text-center">
          <a 
            href="/condiciones-del-desarrollo" 
            className="inline-block bg-neuro-blue text-white py-4 px-10 rounded-full hover:bg-neuro-dark-blue transition-colors text-lg font-semibold"
          >
            Ver Todas las Condiciones del Desarrollo
          </a>
        </div>
      </div>
    </section>
  )
}
